import { AutoRefreshTokenService, createInterceptorCondition, INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG, IncludeBearerTokenCondition, provideKeycloak, UserActivityService, withAutoRefreshToken } from "keycloak-angular";
import { KeycloakInitOptions } from "keycloak-js";
import { environment } from '../../environments/environment';
import { SharedTokens } from './cross-tab-auth';

// Base options shared by both bootstrap paths. `login-required` guarantees the
// user is authenticated; `checkLoginIframe` is off because the session-status
// iframe is unreliable with third-party-cookie blocking.
const BASE_INIT_OPTIONS: KeycloakInitOptions = {
  onLoad: 'login-required',
  checkLoginIframe: false,
};

/**
 * Builds the Keycloak init options for this tab.
 *
 * When `inherited` tokens are supplied (handed over by an already-open tab),
 * keycloak-js initializes with them and force-refreshes once via the token
 * endpoint (a REFRESH_TOKEN event, not LOGIN) — so opening a new tab no longer
 * creates a login-attempt row. `onLoad: 'login-required'` is kept alongside the
 * tokens so that if the inherited refresh token is stale, keycloak-js falls back
 * to the normal login redirect instead of failing.
 */
export function buildKeycloakInitOptions(inherited: SharedTokens | null): KeycloakInitOptions {
  if (!inherited) {
    return { ...BASE_INIT_OPTIONS };
  }

  return {
    ...BASE_INIT_OPTIONS,
    token: inherited.token,
    refreshToken: inherited.refreshToken,
    idToken: inherited.idToken,
  };
}

export const provideKeycloakAndInterceptor = (initOptions: KeycloakInitOptions) => {
  // Escape special regex characters in the API URL
  const escapedApiUrl = environment.apiUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const urlConditions = [
    createInterceptorCondition<IncludeBearerTokenCondition>({
        urlPattern: new RegExp(`^${escapedApiUrl}`, 'i'),
        bearerPrefix: 'Bearer',
      },
    ),
    // you can add more interceptors in this array...
  ];

  // in our case, we put the identity configuration in the environment files
  const { identityServerUrl, clientId, realm } = environment.auth;

  return [
    provideKeycloak({
      config: {
        url: identityServerUrl,
        clientId: clientId,
        realm: realm,
      },
      initOptions,
      features: [
        withAutoRefreshToken({
          sessionTimeout: 36000000,
          onInactivityTimeout: 'logout',
        }),
      ],
      providers: [
        AutoRefreshTokenService,
        UserActivityService,
      ],
    }),
    { provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG, useValue: urlConditions },
  ];
};
